import { create } from 'zustand';
import type { LiveSummary, Platform } from '@multimod/shared';

interface PanelStore {
  broadcastDestinations: Platform[];
  setBroadcastDestinations: (p: Platform[]) => void;
  cycleBroadcastDestinations: () => void;
  discoverySheetOpen: boolean;
  setDiscoverySheetOpen: (open: boolean) => void;
  /**
   * Live primary selecionada + lives anexadas (secondaries). Vivem no store em
   * vez de useState do PanelPage pra sobreviver à navegação pra /settings e
   * outras rotas — sem isso, o desmount do PanelPage zera o contexto.
   *
   * Não persiste em storage: ao recarregar a aba, useActiveLives revalida.
   * Lives "fantasma" entre sessões seriam pior do que F5 limpo.
   *
   * NOTA: dentro de uma mesma sessão a live encerrada também fica no store
   * até troca explícita ou logout. Detecção e drop automático é follow-up
   * (issue separada) — fora do escopo deste fix.
   */
  selectedLive: LiveSummary | null;
  attachedLives: LiveSummary[];
  setSelectedLive: (live: LiveSummary | null) => void;
  // Reset semântico: substitui o array inteiro por uma lista nova. Nome
  // intencionalmente "reset" pra desencorajar uso como append (que furaria a
  // idempotência por liveChatId garantida em addAttachedLive). Use addAttachedLive
  // pra acrescentar, removeAttachedByPlatform pra remover.
  resetAttachedLives: (list: LiveSummary[]) => void;
  addAttachedLive: (live: LiveSummary) => void;
  removeAttachedByPlatform: (platform: Platform) => void;
  // Merge não-destrutivo dos campos voláteis (viewerCount, title) preservando
  // liveChatId/identidade. Usado pelo refetch do useActiveLives.
  patchLivesByLiveChatId: (
    patches: Map<string, { viewerCount: number; title: string }>,
  ) => void;
  // Limpa state derivado da sessão de usuário (lives selecionadas/anexadas).
  // Chamado no onSuccess do logout pra evitar que state do mod A vaze pra
  // mod B que loga na mesma aba — sem isso, PanelPage remontaria lendo
  // liveChatId errado e flasharia UI de outro usuário antes do 401.
  clearPanelSession: () => void;
}

const CYCLE: Platform[][] = [
  ['twitch', 'youtube'],
  ['twitch'],
  ['youtube'],
];

export const usePanelStore = create<PanelStore>((set, get) => ({
  broadcastDestinations: ['twitch', 'youtube'],
  setBroadcastDestinations: (p) => set({ broadcastDestinations: p }),
  cycleBroadcastDestinations: () => {
    const cur = get().broadcastDestinations;
    const idx = CYCLE.findIndex(
      (c) => c.length === cur.length && c.every((p) => cur.includes(p)),
    );
    const next = CYCLE[(idx + 1) % CYCLE.length]!;
    set({ broadcastDestinations: next });
  },
  discoverySheetOpen: false,
  setDiscoverySheetOpen: (open) => set({ discoverySheetOpen: open }),
  selectedLive: null,
  attachedLives: [],
  setSelectedLive: (live) => set({ selectedLive: live }),
  resetAttachedLives: (list) => set({ attachedLives: list }),
  addAttachedLive: (live) =>
    set((s) =>
      s.attachedLives.some((l) => l.liveChatId === live.liveChatId)
        ? {}
        : { attachedLives: [...s.attachedLives, live] },
    ),
  removeAttachedByPlatform: (platform) =>
    set((s) => ({ attachedLives: s.attachedLives.filter((l) => l.platform !== platform) })),
  patchLivesByLiveChatId: (patches) =>
    set((s) => {
      const selectedPatch = s.selectedLive ? patches.get(s.selectedLive.liveChatId) : undefined;
      return {
        selectedLive:
          s.selectedLive && selectedPatch ? { ...s.selectedLive, ...selectedPatch } : s.selectedLive,
        attachedLives: s.attachedLives.map((l) => {
          const p = patches.get(l.liveChatId);
          return p ? { ...l, ...p } : l;
        }),
      };
    }),
  clearPanelSession: () => set({ selectedLive: null, attachedLives: [] }),
}));
